import { memo, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import WORLD from "./worldDots.json";
import { LEVELS, LevelChip, InfoTip, Flag, PANEL, nationGradient } from "./regShared";

type Juris = {
  name: string; code: string; level: string; momentum: string;
  stories_30d: number; enforcement_30d: number; initiatives: number;
};

const W = 940, H = 408, PPD = W / 360, LAT_TOP = 84, PAD = 16;
const px = (lng: number) => (lng + 180) * PPD;
const py = (lat: number) => (LAT_TOP - lat) * PPD + PAD;

const ANCHORS: { code: string; lat: number; lng: number; sigma: number }[] = [
  { code: "US", lat: 39, lng: -98, sigma: 78 },
  { code: "BR", lat: -10, lng: -53, sigma: 52 },
  { code: "UK", lat: 54, lng: -2.5, sigma: 20 },
  { code: "EU", lat: 48.5, lng: 9, sigma: 40 },
  { code: "RU", lat: 58, lng: 82, sigma: 72 },
  { code: "AE", lat: 24, lng: 54, sigma: 22 },
  { code: "IN", lat: 22, lng: 78, sigma: 36 },
  { code: "CN", lat: 34, lng: 104, sigma: 50 },
  { code: "HK", lat: 22.3, lng: 114.2, sigma: 10 },
  { code: "KR", lat: 36.5, lng: 127.8, sigma: 13 },
  { code: "JP", lat: 36.5, lng: 138.5, sigma: 19 },
  { code: "SG", lat: 1.35, lng: 103.8, sigma: 10 },
  { code: "AU", lat: -25, lng: 134, sigma: 42 },
  { code: "CA", lat: 58, lng: -106, sigma: 55 },
  { code: "CH", lat: 46.8, lng: 8.2, sigma: 11 },
  { code: "DE", lat: 51.2, lng: 10.4, sigma: 20 },
  { code: "FR", lat: 46.6, lng: 2.4, sigma: 20 },
  { code: "NG", lat: 9.1, lng: 8.7, sigma: 22 },
  { code: "ZA", lat: -29, lng: 24, sigma: 24 },
  { code: "TR", lat: 39, lng: 35.2, sigma: 20 },
  { code: "AR", lat: -34.6, lng: -64.2, sigma: 30 },
  { code: "MX", lat: 23.6, lng: -102.5, sigma: 28 },
  { code: "TH", lat: 15.8, lng: 101, sigma: 16 },
  { code: "PH", lat: 12.9, lng: 122, sigma: 16 },
  { code: "VN", lat: 16.2, lng: 107.8, sigma: 14 },
  { code: "ID", lat: -2.5, lng: 118, sigma: 28 },
  { code: "MY", lat: 4.2, lng: 102, sigma: 13 },
  { code: "IL", lat: 31.4, lng: 35, sigma: 9 },
  { code: "SA", lat: 24, lng: 45, sigma: 24 },
  { code: "KZ", lat: 48, lng: 67, sigma: 28 },
  { code: "PK", lat: 30.4, lng: 69.4, sigma: 20 },
  { code: "SV", lat: 13.8, lng: -88.9, sigma: 7 },
  { code: "TW", lat: 23.7, lng: 121, sigma: 9 },
  { code: "NZ", lat: -41.5, lng: 173, sigma: 13 },
  { code: "UA", lat: 48.4, lng: 31.2, sigma: 18 },
  { code: "KE", lat: 0.2, lng: 37.9, sigma: 15 },
];
const AX: Record<string, { x: number; y: number; sigma: number }> = {};
ANCHORS.forEach((a) => { AX[a.code] = { x: px(a.lng), y: py(a.lat), sigma: a.sigma }; });

const LEVEL_T: Record<string, number> = { low: 0.3, moderate: 0.52, high: 0.76, very_high: 1.0 };
const RAMP: [number, [number, number, number]][] = [
  [0.0, [22, 52, 128]], [0.3, [36, 98, 229]], [0.55, [20, 207, 230]],
  [0.78, [139, 92, 255]], [1.0, [243, 61, 180]],
];

const rampColor = (t: number) => {
  const tt = Math.min(Math.max(t, 0), 1);
  for (let i = 1; i < RAMP.length; i++) {
    if (tt <= RAMP[i][0]) {
      const [t0, c0] = RAMP[i - 1]; const [t1, c1] = RAMP[i];
      const f = (tt - t0) / (t1 - t0);
      const c = c0.map((v, k) => Math.round(v + (c1[k] - v) * f));
      return `rgb(${c[0]},${c[1]},${c[2]})`;
    }
  }
  return "rgb(243,61,180)";
};

const GRID_STEP = 52;
const clampPan = (p: { x: number; y: number }, z: number) => {
  const mx = (W * (z - 1)) / 2 + W * 0.1;
  const my = (H * (z - 1)) / 2 + H * 0.1;
  return {
    x: Math.min(Math.max(p.x, -mx), mx),
    y: Math.min(Math.max(p.y, -my), my),
  };
};

const MAP_PANEL_STYLE = {
  background: "#000000",
  borderColor: "rgba(28,72,172,0.6)",
};

export const RegHeatMap = ({ jurisdictions, selected, onSelect }: {
  jurisdictions: Juris[]; selected: string | null; onSelect: (code: string) => void;
}) => {
  const [hovered, setHovered] = useState<string | null>(null);
  const [z, setZ] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [interacting, setInteracting] = useState(false);
  const surfRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const baseCvRef = useRef<HTMLCanvasElement | null>(null);
  const baseKeyRef = useRef<string>("");
  const ptrs = useRef(new Map<number, { x: number; y: number }>());
  const pinch = useRef<number | null>(null);
  const draggedRef = useRef(false);

  useEffect(() => { setPan((p) => clampPan(p, z)); }, [z]);
  const zoomBy = (f: number) => setZ((prev) => Math.min(Math.max(prev * f, 1), 4));
  const resetView = () => { setZ(1); setPan({ x: 0, y: 0 }); };

  useEffect(() => {
    const clear = (e: PointerEvent) => {
      ptrs.current.delete(e.pointerId);
      pinch.current = null;
      if (ptrs.current.size === 0) setInteracting(false);
    };
    window.addEventListener("pointerup", clear);
    window.addEventListener("pointercancel", clear);
    return () => {
      window.removeEventListener("pointerup", clear);
      window.removeEventListener("pointercancel", clear);
    };
  }, []);

  useEffect(() => {
    const el = surfRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      setZ((prev) => Math.min(Math.max(prev * (e.deltaY < 0 ? 1.18 : 1 / 1.18), 1), 4));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const toSvg = (v: number) => {
    const rect = surfRef.current?.getBoundingClientRect();
    return rect && rect.width ? (v * W) / rect.width : v;
  };
  const onPointerDown = (e: React.PointerEvent) => {
    ptrs.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    draggedRef.current = false;
    setInteracting(true);
    if (ptrs.current.size === 2) {
      const [a, b] = [...ptrs.current.values()];
      pinch.current = Math.hypot(a.x - b.x, a.y - b.y);
    }
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const prev = ptrs.current.get(e.pointerId);
    if (!prev) return;
    if (e.pointerType === "mouse" && e.buttons === 0) {
      ptrs.current.delete(e.pointerId);
      if (ptrs.current.size === 0) setInteracting(false);
      return;
    }
    ptrs.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (ptrs.current.size === 2) {
      const [a, b] = [...ptrs.current.values()];
      const d = Math.hypot(a.x - b.x, a.y - b.y);
      if (pinch.current) zoomBy(d / pinch.current);
      pinch.current = d;
      draggedRef.current = true;
      return;
    }
    const dx = e.clientX - prev.x, dy = e.clientY - prev.y;
    if (Math.abs(dx) + Math.abs(dy) > 2) draggedRef.current = true;
    setPan((p) => clampPan({ x: p.x + toSvg(dx), y: p.y + toSvg(dy) }, z));
  };
  const endPointer = (e: React.PointerEvent) => {
    ptrs.current.delete(e.pointerId);
    pinch.current = null;
    if (ptrs.current.size === 0) setInteracting(false);
  };

  const byCode = useMemo(() => Object.fromEntries(jurisdictions.map((j) => [j.code, j])), [jurisdictions]);
  const levels = useMemo(() => {
    const out: Record<string, string> = {};
    jurisdictions.forEach((j) => { out[j.code] = j.level; });
    return out;
  }, [jurisdictions]);
  const levelKey = ANCHORS.map((a) => levels[a.code] || "-").join("|");

  const dotField = useMemo(() => {
    return (WORLD as any).dots.map(([x, y]: number[]) => {
      let t = 0;
      for (const a of ANCHORS) {
        const lv = levels[a.code];
        if (!lv) continue;
        const p = AX[a.code];
        const dx = x - p.x, dy = y - p.y;
        const d2 = dx * dx + dy * dy;
        const s = p.sigma * 0.72;
        const v = LEVEL_T[lv] * Math.exp(-d2 / (2 * s * s));
        if (v > t) t = v;
      }
      t = Math.pow(t, 1.18);
      const tt = Math.max(t, 0.13);
      return { x, y, fill: rampColor(tt), op: 0.5 + 0.5 * Math.min(tt * 1.12, 1) };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [levelKey]);

  useEffect(() => {
    const cv = canvasRef.current, wrap = surfRef.current;
    if (!cv || !wrap) return;
    const R = 1.6, TAU = Math.PI * 2;
    const paintDots = (ctx: CanvasRenderingContext2D) => {
      for (const d of dotField) {
        ctx.globalAlpha = d.op;
        ctx.fillStyle = d.fill;
        ctx.beginPath();
        ctx.arc(d.x, d.y, R, 0, TAU);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    };
    const renderBase = (bw: number, bh: number) => {
      let b = baseCvRef.current;
      if (!b) { b = document.createElement("canvas"); baseCvRef.current = b; }
      if (b.width !== bw || b.height !== bh) { b.width = bw; b.height = bh; }
      const bctx = b.getContext("2d");
      if (!bctx) return;
      bctx.setTransform(1, 0, 0, 1, 0, 0);
      bctx.clearRect(0, 0, bw, bh);
      bctx.setTransform(bw / W, 0, 0, bw / W, 0, 0);
      paintDots(bctx);
    };
    const draw = () => {
      const cw = wrap.clientWidth;
      if (!cw) return;
      const ch = cw * (H / W);
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const bw = Math.round(cw * dpr), bh = Math.round(ch * dpr);
      if (cv.width !== bw || cv.height !== bh) { cv.width = bw; cv.height = bh; }
      const ctx = cv.getContext("2d");
      if (!ctx) return;
      const key = `${bw}x${bh}:${levelKey}`;
      if (baseKeyRef.current !== key) { renderBase(bw, bh); baseKeyRef.current = key; }
      const k = bw / W;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, bw, bh);
      ctx.setTransform(k, 0, 0, k, 0, 0);
      ctx.translate(W / 2 + pan.x, H / 2 + pan.y);
      ctx.scale(z, z);
      ctx.translate(-W / 2, -H / 2);
      const base = baseCvRef.current;
      if (base && (interacting || z <= 1.01)) {
        ctx.drawImage(base, 0, 0, base.width, base.height, 0, 0, W, H);
      } else {
        paintDots(ctx);
      }
    };
    draw();
    const ro = new ResizeObserver(draw);
    ro.observe(wrap);
    return () => ro.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dotField, z, pan.x, pan.y, interacting, levelKey]);

  const cardCode = hovered || null;
  const cardJ = cardCode ? byCode[cardCode] : null;
  const cardA = cardCode ? AX[cardCode] : null;
  const tp = cardA ? {
    x: Math.min(Math.max((cardA.x - W / 2) * z + W / 2 + pan.x, 0), W),
    y: Math.min(Math.max((cardA.y - H / 2) * z + H / 2 + pan.y, 0), H),
  } : null;

  return (
    <div data-testid="reg-heat-map" className={`${PANEL} p-5 flex flex-col min-w-0`} style={MAP_PANEL_STYLE}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="nl-geist font-bold text-[14px] text-[#eef1f9] tracking-[0.04em]">GLOBAL REGULATORY HEAT MAP</h3>
            <InfoTip label="Regulatory Activity Index methodology" testid="reg-map-info">
              Activity levels rank each jurisdiction by NewsLayer policy coverage over the
              last 30 days — Very High is the most active tier. Drag to pan when zoomed;
              pinch or Ctrl + scroll to zoom. Derived from coverage, not an official government metric.
            </InfoTip>
          </div>
          <p className="nl-geist text-[11px] text-[#8b93a9] mt-1">Overall Regulatory Activity Index</p>
        </div>
        <Link to="/regulation-tracker" data-testid="reg-map-full-link"
          className="shrink-0 inline-flex items-center gap-1 h-7 px-3.5 rounded-lg ring-1 ring-white/[0.14] bg-[#0a0a10] text-[#d5dbeb] nl-geist font-semibold text-[10px] hover:bg-[#15151d] hover:ring-[#8b5cff]/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8b5cff] motion-safe:transition-colors">
          View full map <span aria-hidden>↗</span>
        </Link>
      </div>

      <div className="relative mt-4 -mx-5 -mb-5" onPointerLeave={() => setHovered(null)}>
        <div ref={surfRef}
          className="relative rounded-b-2xl overflow-hidden border-t border-[#1c48ac]/30 select-none"
          style={{
            background: "#000000",
            touchAction: "none",
            cursor: interacting ? "grabbing" : "grab",
          }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endPointer}
          onPointerCancel={endPointer}>
        <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden />
        <svg viewBox={`0 0 ${W} ${H}`} className="relative w-full h-auto block" role="group"
          aria-label="World map of regulatory activity by jurisdiction. Drag to pan when zoomed, pinch or Ctrl + scroll to zoom.">
          <defs>
            {Object.entries(LEVELS).map(([k, v]) => (
              <radialGradient key={k} id={`reg-bloom-${k}`}>
                <stop offset="0%" stopColor={v.color} stopOpacity="0.2" />
                <stop offset="55%" stopColor={v.color} stopOpacity="0.06" />
                <stop offset="100%" stopColor={v.color} stopOpacity="0" />
              </radialGradient>
            ))}
            <radialGradient id="reg-hover-glow">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="0.13" />
              <stop offset="60%" stopColor="#ffffff" stopOpacity="0.04" />
              <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
            </radialGradient>
          </defs>
          <g style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${z})`, transformOrigin: `${W / 2}px ${H / 2}px` }}>
            <g stroke="#131a29" strokeOpacity="0.24" strokeWidth="0.5">
              {Array.from({ length: Math.floor(W / GRID_STEP) }, (_, i) => (
                <line key={`gx${i}`} x1={(i + 1) * GRID_STEP} y1="0" x2={(i + 1) * GRID_STEP} y2={H} />
              ))}
              {Array.from({ length: Math.floor(H / GRID_STEP) }, (_, i) => (
                <line key={`gy${i}`} x1="0" y1={(i + 1) * GRID_STEP} x2={W} y2={(i + 1) * GRID_STEP} />
              ))}
            </g>
            {ANCHORS.filter((a) => ["high", "very_high"].includes(levels[a.code] || "")).map((a) => {
              const p = AX[a.code];
              return (
                <circle key={`bloom-${a.code}`} cx={p.x} cy={p.y} r={p.sigma * 1.5}
                  fill={`url(#reg-bloom-${levels[a.code]})`} />
              );
            })}
            {cardA && (
              <circle cx={cardA.x} cy={cardA.y} r={cardA.sigma * 1.15} fill="url(#reg-hover-glow)"
                style={{ mixBlendMode: "screen" }} className="pointer-events-none" />
            )}
            {ANCHORS.map((a) => {
              const j = byCode[a.code];
              if (!j) return null;
              const p = AX[a.code];
              const color = LEVELS[j.level]?.color || LEVELS.low.color;
              const isSel = selected === a.code;
              const isHot = cardCode === a.code;
              return (
                <g key={a.code} data-testid={`reg-map-target-${a.code}`}
                  role="button" tabIndex={0}
                  aria-label={`${j.name} — ${LEVELS[j.level]?.label || "LOW"} regulatory activity. Press Enter to open spotlight.`}
                  aria-pressed={isSel}
                  className="cursor-pointer focus:outline-none"
                  onPointerEnter={(e) => { if (e.pointerType !== "touch") setHovered(a.code); }}
                  onFocus={() => setHovered(a.code)}
                  onBlur={() => setHovered(null)}
                  onClick={(e) => { e.stopPropagation(); if (draggedRef.current) return; onSelect(a.code); }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(a.code); }
                    if (e.key === "Escape") setHovered(null);
                  }}>
                  <circle cx={p.x} cy={p.y} r={16 / z} fill="transparent" />
                  {(isSel || isHot) && (
                    <circle cx={p.x} cy={p.y} r={8.5 / Math.sqrt(z)} fill="none" stroke={color}
                      strokeOpacity="0.85" strokeWidth={1.2 / Math.sqrt(z)} />
                  )}
                  {isSel && (
                    <circle cx={p.x} cy={p.y} r={13 / Math.sqrt(z)} fill="none" stroke={color}
                      strokeOpacity="0.35" strokeWidth={1 / z} />
                  )}
                  <circle cx={p.x} cy={p.y} r={(isSel || isHot ? 2.6 : 1.9) / Math.sqrt(z)}
                    fill="#ffffff" fillOpacity={isSel || isHot ? 0.95 : 0.55}
                    className="motion-safe:transition-[fill-opacity] motion-safe:duration-200" />
                </g>
              );
            })}
          </g>
        </svg>

        <div className="absolute right-4 bottom-3 flex rounded-lg ring-1 ring-white/[0.12] bg-[#050508]/95 overflow-hidden shadow-lg" data-testid="reg-map-zoom">
          <button type="button" aria-label="Zoom in" data-testid="reg-map-zoom-in" disabled={z >= 4}
            onClick={() => zoomBy(1.4)}
            className="w-[30px] h-[28px] nl-geist font-semibold text-[15px] text-[#e8ecf6] hover:bg-white/10 disabled:opacity-35 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-[#8b5cff]">+</button>
          <button type="button" aria-label="Zoom out" data-testid="reg-map-zoom-out" disabled={z <= 1}
            onClick={() => zoomBy(1 / 1.4)}
            className="w-[30px] h-[28px] nl-geist font-semibold text-[15px] text-[#e8ecf6] hover:bg-white/10 disabled:opacity-35 border-l border-white/[0.1] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-[#8b5cff]">−</button>
          <button type="button" aria-label="Reset view" data-testid="reg-map-zoom-reset" onClick={resetView}
            className="w-[30px] h-[28px] nl-geist font-semibold text-[13px] text-[#e8ecf6] hover:bg-white/10 border-l border-white/[0.1] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-[#8b5cff]">◎</button>
        </div>
        <div className="absolute left-4 bottom-3 hidden sm:flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md px-2.5 py-1.5 bg-[#020204]/65 backdrop-blur-[2px] pointer-events-none" data-testid="reg-map-legend">
          {Object.entries(LEVELS).map(([k, v]) => (
            <span key={k} className="inline-flex items-center gap-1.5">
              <span className="w-[8px] h-[8px] rounded-full" style={{ backgroundColor: v.color, boxShadow: `0 0 7px ${v.color}59` }} aria-hidden />
              <span className="nl-geist font-semibold text-[9.5px] tracking-[0.06em] text-[#c3c9d8]">{v.label}</span>
            </span>
          ))}
        </div>
        </div>

        <div className="mt-3 px-5 pb-4 flex sm:hidden flex-wrap items-center gap-x-4 gap-y-1.5">
          {Object.entries(LEVELS).map(([k, v]) => (
            <span key={k} className="inline-flex items-center gap-1.5">
              <span className="w-[8px] h-[8px] rounded-full" style={{ backgroundColor: v.color, boxShadow: `0 0 7px ${v.color}59` }} aria-hidden />
              <span className="nl-geist font-semibold text-[9.5px] tracking-[0.06em] text-[#c3c9d8]">{v.label}</span>
            </span>
          ))}
        </div>

        {cardJ && tp && (
          <div data-testid="reg-map-card" role="status"
            onPointerEnter={() => setHovered(cardCode)}
            onPointerLeave={() => setHovered(null)}
            className="absolute z-20 w-[268px] max-w-[74%] rounded-xl p-px shadow-[0_14px_40px_rgba(3,2,10,0.85)] motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-95 motion-safe:duration-200"
            style={{
              background: `linear-gradient(135deg, ${nationGradient(cardJ.code)})`,
              left: tp.x < W / 2 ? `min(${(tp.x / W) * 100 + 3}%, 64%)` : undefined,
              right: tp.x >= W / 2 ? `min(${100 - (tp.x / W) * 100 + 3}%, 64%)` : undefined,
              top: tp.y < H * 0.49 ? `min(${(tp.y / H) * 100 + 9}%, 44%)` : undefined,
              bottom: tp.y >= H * 0.49 ? `${100 - (tp.y / H) * 100 + 9}%` : undefined,
            }}>
            <div className="rounded-[11px] p-4"
              style={{ background: "linear-gradient(180deg, rgba(10,9,16,0.99) 0%, rgba(4,4,8,0.99) 100%)" }}>
            <div className="flex items-center gap-2.5">
              <Flag code={cardJ.code} className="w-[26px] h-[18px]" />
              <p className="nl-geist font-bold text-[12px] text-[#f2f5fc] truncate flex-1">{cardJ.name}</p>
              <LevelChip level={cardJ.level} className="!h-[18px] !px-1.5 !text-[7.5px]" />
            </div>
            <dl className="mt-3 space-y-[7px]">
              <div className="flex items-center justify-between">
                <dt className="nl-geist text-[10px] text-[#8b93a9]">Policy Momentum</dt>
                <dd className="nl-geist font-semibold text-[11px] text-[#f2f5fc]">{cardJ.momentum}</dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="nl-geist text-[10px] text-[#8b93a9]">Active Initiatives</dt>
                <dd className="nl-geist font-semibold text-[11px] text-[#f2f5fc]">{cardJ.initiatives || "—"}</dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="nl-geist text-[10px] text-[#8b93a9]">Enforcement Stories (30D)</dt>
                <dd className="nl-geist font-semibold text-[11px] text-[#f2f5fc]">{cardJ.enforcement_30d}</dd>
              </div>
            </dl>
            <div className="mt-3 pt-2.5 border-t border-white/[0.07] text-right">
              <Link to={`/jurisdiction/${cardJ.code}`}
                data-testid="reg-map-card-details"
                className="nl-geist font-semibold text-[10px] text-[#14cfe6] hover:text-white motion-safe:transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#14cfe6] rounded">
                View details →
              </Link>
            </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
